'use client';

import { useState, useEffect } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { motion, AnimatePresence } from 'framer-motion';
import { type Industry } from './IndustriesShowcase';
import { useMediaQuery } from '@/hooks/use-media-query';
import { Heading, Button } from '@/components/ui/design-system';
import { ArrowRight } from 'lucide-react';

const defaultImage = '/images/pages/industries/featured/default.jpg';

interface IndustriesClientProps {
  industries: Industry[];
}

export function IndustriesClient({ industries }: IndustriesClientProps) {
  const [activeIndustry, setActiveIndustry] = useState<Industry | null>(null);
  const router = useRouter();
  const isDesktop = useMediaQuery('(min-width: 768px) and (hover: hover)');

  // Set initial active industry for mobile view
  useEffect(() => {
    if (!isDesktop && industries.length > 0) {
      setActiveIndustry(industries[0]);
    }
    if (isDesktop) {
      setActiveIndustry(null);
    }
  }, [isDesktop, industries]);

  return (
    <div 
      className="relative h-auto md:h-[500px] w-full overflow-hidden rounded-lg shadow-lg bg-background"
      onMouseLeave={() => {
        if (isDesktop) {
          setActiveIndustry(null); // Reset on mouse leave for desktop
        }
      }}
    >
      {/* Preload images to prevent lag on hover/focus */}
            {/* Preload images for the expanded view to prevent lag on hover/focus */}
      <div style={{ display: 'none' }}>
        {industries.map(industry => (
          industry.image_url && (
            <link
              key={`preload-${industry.id}`}
              rel="preload"
              as="image"
              href={industry.image_url}
              aria-hidden="true"
            />
          )
        ))}
      </div>

      {isDesktop ? (
        // --- DESKTOP LOGIC ---
        <>
          {/* The 4-cell grid view (visible initially) */}
          <div className="absolute inset-0 grid grid-cols-4">
            {industries.map((industry) => (
              <Link
                key={industry.id}
                href={`/industries?industry=${industry.slug}#industries-selector`}
                target="_blank"
                rel="noopener noreferrer"
                className="relative flex flex-col items-start justify-end p-8 group cursor-pointer border-r border-white/10"
                onMouseEnter={() => setActiveIndustry(industry)}
                onFocus={() => setActiveIndustry(industry)}
                aria-label={`Explore the ${industry.name} industry`}
              >
                <Image
                  src={industry.image_url || defaultImage}
                  alt={`A representative image for the ${industry.name} industry`}
                  fill
                  sizes="(min-width: 1024px) 25vw, 100vw"
                  className="z-0 transition-transform duration-300 group-hover:scale-105 object-cover"
                />
                {/* Single gradient overlay to provide text contrast and fix dark mode */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-black/20 to-transparent z-10" />
                <div className="relative z-20">
                  <Heading
                    level={4}
                    className="text-white font-semibold inline-block px-3 py-1 rounded-lg bg-black/30 backdrop-blur-sm border border-white/10"
                  >
                    {industry.name}
                  </Heading>
                </div>
              </Link>
            ))}
          </div>

          {/* The expanded image overlay (visible on hover) */}
          <AnimatePresence>
            {activeIndustry && (
              <motion.div
                className="absolute inset-0 z-30 cursor-pointer"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.4, ease: 'easeOut' }}
                onClick={() => window.open(`/industries?industry=${activeIndustry.slug}#industries-selector`, '_blank', 'noopener,noreferrer')}
                role="link"
                aria-label={`Learn more about the ${activeIndustry.name} industry`}
              >
                <Image
                  key={activeIndustry.id}
                  src={activeIndustry.image_url || defaultImage}
                  alt={`Enlarged view for the ${activeIndustry.name} industry`}
                  fill
                  className="object-cover"
                  sizes="100vw"
                  priority
                />
                {/* Adjusted overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/30 to-transparent dark:from-black/60 dark:via-black/40 dark:to-transparent" />
                
                {/* Other Industries Links */}
                <div className="absolute top-4 right-4 z-20">
                  <div className="flex flex-col items-end space-y-2">
                    {industries
                      .filter(i => i.id !== activeIndustry.id)
                      .map(i => (
                        <button
                          key={i.id}
                          onClick={(e) => {
                            e.stopPropagation(); // Stop event from bubbling to the parent div's onClick
                            setActiveIndustry(i);
                          }}
                          className="px-3 py-1 text-sm text-white bg-black/30 rounded-full hover:bg-black/50 transition-colors duration-200 backdrop-blur-sm focus:outline-none focus:ring-2 focus:ring-white/50"
                          aria-label={`Switch to the ${i.name} industry view`}
                        >
                          {i.name}
                        </button>
                      ))}
                  </div>
                </div>

                <div className="relative z-10 flex flex-col items-start justify-end h-full p-8 pointer-events-none">
                  <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2, ease: 'easeOut' }}>
                    <Heading level={2} className="text-white font-bold mb-2 inline-block p-2 rounded-md bg-black/20 backdrop-blur-sm">
                      {activeIndustry.name}
                    </Heading>
                    <p className="text-white/80 max-w-lg mb-4">
                      {activeIndustry.description}
                    </p>
                    <Button variant="primary" size="sm">
                      Explore <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </motion.div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </>
      ) : (
        // --- MOBILE/TABLET LOGIC ---
        <div className="flex flex-col w-full">
          {industries.map((industry) => (
            <Link
              key={industry.id}
              href={`/industries?industry=${industry.slug}#industries-selector`}
              target="_blank"
              rel="noopener noreferrer"
              className="relative flex flex-col group overflow-hidden border-b border-border last:border-b-0"
            >
              <div className="relative w-full h-48 overflow-hidden">
                                <Image
                  src={industry.image_url || defaultImage}
                  alt={`A representative image for the ${industry.name} industry`}
                  fill
                  sizes="(max-width: 767px) 100vw, 50vw"
                  className="transition-transform duration-300 group-hover:scale-105 object-cover"
                />
                <div className="absolute inset-0 bg-black/20" />
              </div>
              <div className="p-4 bg-background">
                <Heading level={4} className="font-semibold text-foreground mb-1">
                  {industry.name}
                </Heading>
                <p className="text-sm text-muted-foreground line-clamp-2">
                  {industry.description}
                </p>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
